import {
  replaceDomainForCacheKey,
  useErrorOnNotOkFetchye,
} from '@americanexpress/fetchye-amex';
import { useSelector } from 'react-redux';
import { useRef } from 'react';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { getActiveLocaleSelector, createConfigSelector } from '@americanexpress/travel-selectors';
import { createSelector } from 'reselect';
import { hasAirlineDropdownRule, hasStrategicInitiativeRule } from '../constants/ruleConstants';
import { MODULE_NAME } from '../constants/packagesConstants';

export const getEwpDomainUrl = createConfigSelector('ewpDomain');

export const getAirlineUrlSelector = createSelector(
  getEwpDomainUrl,
  getActiveLocaleSelector,
  (tulipsRdsUrl, locale) => {
    const updatedLocale = locale.toLowerCase().replace('-', '_');
    return `${tulipsRdsUrl}/travel/v1/tulips_rds/refcodes/SWFARLN?locale=${updatedLocale}&context_cd=SWF&active=Y`;
  }
);

export const fetchyeReporting = ({ key, options, error }) => ({
  moduleID: MODULE_NAME,
  component: 'useAirlinesApi',
  fetch: {
    url: key,
    status: error?.status,
    correlationId: options.headers?.['Correlation-ID'] || '',
  },
});

const fetchyeOpts = {
  mapKeyToCacheKey: replaceDomainForCacheKey('ewpDomains'),
};

const transformData = (data) => data?.map((item) => {
  const descObject = JSON.parse(item.refcddesc);
  return {
    name: descObject.desc,
    code: item.refcd,
  };
})?.sort((firstAirline, secondAirline) => firstAirline.name.localeCompare(secondAirline.name));

export const useAirlines = () => {
  const retryAttempted = useRef(false);
  const airlineUrl = useSelector(getAirlineUrlSelector);
  const hasAirlineDropdown = useConditionalFeature(hasAirlineDropdownRule);
  const hasStrategicInitiative = useConditionalFeature(hasStrategicInitiativeRule);
  const fetchAirlines = !hasStrategicInitiative && hasAirlineDropdown;

  const {
    isLoading,
    data,
    error,
    run: retryApiCall,
  } = useErrorOnNotOkFetchye(
    `${airlineUrl}`,
    {
      ...fetchyeOpts,
      defer: !fetchAirlines,
    },
    fetchyeReporting
  );

  if (error && !retryAttempted.current) {
    retryAttempted.current = true;
    retryApiCall();
  }

  const validatedData = Array.isArray(data?.body) ? data.body : [];

  return {
    isLoading,
    data: transformData(validatedData),
  };
};
